import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { apiPostPhysicians } from "../../../api";
import { NameFields } from "../../../components/name-fields";
import { Button } from "../../../components/ui-kit/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "../../../components/ui-kit/dialog";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { TagsInput } from "../../../components/ui-kit/fields/tags-input";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormField } from "../../../components/ui-kit/form/form-field";
import { FormGrid } from "../../../components/ui-kit/form/form-grid";
import { managingRoles } from "../../../constants/constants";
import { splitNameArray } from "../../../utils/names";
import { maybeAsArray } from "../../../utils/utils";
import { CountrySelect } from "../../patient/country-select";
import { RegionSelect } from "../../patient/region-select";
import { AdminConsole } from "../admin-console";
import { ClinicsModal } from "../common/clinics-modal";
import { PatientsModal } from "../common/patients-modal";
import { UsersModal } from "../common/users-modal";

export const AddPhysicianDialog = observer(function AddPhysicianDialog({
  admin,
  onClose,
  onSaved,
}: {
  admin: AdminConsole;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const [fields] = useState(() =>
    observable({
      given_name: "",
      family_name: "",
      middle_names: null as string | null,
      name_particle: null as string | null,
      additional_family_names: null as string | null,
      name_suffix: null as string | null,
      title_before: null as string | null,
      title_after: null as string | null,
      name_order_given_first: true,
      country: "",
      region: null as number | null,
      email: "",
      phone: [] as string[],
      connected_user_uuid: null as string | null,
      clinics_uuids: [] as string[],
      patient_ids: [] as number[],
      assigned_users_uuids: [] as string[],
    }),
  );

  async function save() {
    await apiPostPhysicians({
      body: {
        ...fields,
        region: fields.region ?? undefined,
        middle_names: splitNameArray(fields.middle_names),
        additional_family_names: splitNameArray(fields.additional_family_names),
      },
    });
    onSaved();
    onClose();
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      // Wider than the 512px default, which clipped the last ChooseField.
      width="672px"
      className="max-h-[90dvh]"
    >
      <DialogHeader>
        <Trans>Add new physician</Trans>
      </DialogHeader>
      <Form className="flex flex-col min-h-0 flex-1" onSubmit={() => save()}>
        <DialogBody className="overflow-x-hidden">
          <div className="flex max-w-xl flex-col gap-4">
            <NameFields model={fields} />

            <FormGrid>
              <FormField label={t`Country`} mandatory>
                <CountrySelect
                  attr="country"
                  regionAttr="region"
                  model={fields}
                  required={true}
                />
              </FormField>

              <FormField label={t`Region`} mandatory>
                <RegionSelect
                  attr="region"
                  country={fields.country || undefined}
                  model={fields}
                  required
                />
              </FormField>

              <FormField label={t`Email`} mandatory>
                <TextInput attr="email" model={fields} required />
              </FormField>

              <FormField label={t`Phone(s)`} mandatory>
                <TagsInput attr="phone" model={fields} />
              </FormField>
            </FormGrid>

            <div className="border-border border-t" />

            <div className="flex flex-col gap-4 sm:flex-row sm:gap-6 sm:*:flex-1">
              <FormField label={t`Connected user`}>
                <ChooseField
                  label={
                    fields.connected_user_uuid
                      ? admin.users?.userMapping[fields.connected_user_uuid]
                      : t`None`
                  }
                  modal={({ onClose }) => {
                    const users = admin.createUsers();
                    return (
                      <UsersModal
                        title={t`Choose connected user for a new physician`}
                        preselected={
                          maybeAsArray(fields.connected_user_uuid) ?? undefined
                        }
                        filter={{
                          role: ["study-physician", "physician"],
                        }}
                        mode="single"
                        model={users}
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          fields.connected_user_uuid = selected[0];
                        }}
                      />
                    );
                  }}
                />
              </FormField>

              <FormField label={t`Clinics`}>
                <ChooseField
                  count={fields.clinics_uuids.length}
                  modal={({ onClose }) => {
                    const clinics = admin.createClinics();
                    return (
                      <ClinicsModal
                        preselected={fields.clinics_uuids}
                        filter={{ country: [fields.country] }}
                        mode="multi"
                        model={clinics}
                        title={t`Choose clinics for a new physician`}
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          fields.clinics_uuids = selected;
                        }}
                      />
                    );
                  }}
                />
              </FormField>

              <FormField label={t`Monitoring user(s)`}>
                <ChooseField
                  count={fields.assigned_users_uuids.length}
                  modal={({ onClose }) => {
                    const users = admin.createMonitoringUsers(
                      fields.country,
                      fields.region ?? undefined,
                    );
                    return (
                      <UsersModal
                        title={t`Choose monitoring user(s) for a new physician`}
                        preselected={fields.assigned_users_uuids}
                        filter={{ role: managingRoles }}
                        mode="multi"
                        model={users}
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          fields.assigned_users_uuids = selected;
                        }}
                      />
                    );
                  }}
                />
              </FormField>

              <FormField label={t`Patients`}>
                <ChooseField
                  count={fields.patient_ids.length}
                  modal={({ onClose }) => {
                    const patients = admin.createPatients();
                    return (
                      <PatientsModal
                        title={t`Choose patients for a new physician`}
                        model={patients}
                        preselected={fields.patient_ids}
                        filter={{ country: [fields.country] }}
                        mode="multi"
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          fields.patient_ids = selected;
                        }}
                      />
                    );
                  }}
                />
              </FormField>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="primary" type="submit">
            <Trans>Add physician</Trans>
          </Button>
        </DialogFooter>
      </Form>
    </Dialog>
  );
});
